import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import authService from './services/AuthService';
import chatbotService from './services/ChatbotService';
import Users from './components/Users';
import Files from './Files';
import Conversations from './Conversations';
import ConversationHistory from './components/ConversationHistory';
import Analytics from './components/Analytics';
import AuditLogs from './components/AuditLogs';
import Usage from './Usage';
import Chatbot from './components/Chatbot';
import Chatbots from './components/Chatbots';
import Agents from './components/Agents';
import VoiceBots from './components/VoiceBots';
import AdminChatbotForm from './components/AdminChatbotForm';
import AdminAgentForm from './components/AdminAgentForm';
import AdminClientConfig from './components/AdminClientConfig';
import AgentRequests from './components/AgentRequests';
import Tickets from './components/Tickets';
import TicketDetails from './components/TicketDetails';
import SupportTicketDetails from './components/SupportTicketDetails';
import PublicSupportTicketDetails from './components/PublicSupportTicketDetails';
import ticketService from './services/TicketService';
import Plans from './components/Plans';
import VectorStores from './components/VectorStores';
import BaseList from './components/BaseList';
import CustomerKbases from './components/CustomerKbases';
import KbaseDocuments from './components/KbaseDocuments';
import KbaseWorkspace from './components/KbaseWorkspace';
import Sidebar from './components/Sidebar';
import WelcomeVideoModal from './components/WelcomeVideoModal';
import HelpCenter from './components/HelpCenter';
import AdminHelpCenter from './components/AdminHelpCenter';
import { HelpProvider } from './context/HelpProvider';
import { AccessProvider, useAccess } from './context/AccessProvider';
import RequirePermission from './components/RequirePermission';
import PermissionGroups from './components/PermissionGroups';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { ThemeProviderWrapper } from './context/ThemeProviderWrapper';
import { jwtDecode } from 'jwt-decode';
import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from 'react-router-dom';
import './i18n';
import Settings from './components/Settings';
import netbotLogo from './assets/netbot-logo-wide.png';

// ─── Japanese wave SVG background ────────────────────────────────────────────
const WaveBg = () => (
    <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.06 }}
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
    >
        {[0, 60, 120, 180, 240, 300].map((offset, i) => (
            <path
                key={i}
                d={`M0,${200 + offset} C100,${160 + offset} 200,${240 + offset} 300,${200 + offset} S500,${160 + offset} 600,${200 + offset} S700,${240 + offset} 800,${200 + offset} V600 H0 Z`}
                fill="white"
                opacity={0.4 - i * 0.05}
            />
        ))}
        {/* Decorative circles (like tsuba/family crest elements) */}
        <circle cx="650" cy="120" r="80" fill="none" stroke="white" strokeWidth="1.5" opacity="0.3" />
        <circle cx="650" cy="120" r="55" fill="none" stroke="white" strokeWidth="1" opacity="0.2" />
        <circle cx="150" cy="480" r="60" fill="none" stroke="white" strokeWidth="1.5" opacity="0.25" />
        <circle cx="150" cy="480" r="40" fill="none" stroke="white" strokeWidth="1" opacity="0.15" />
    </svg>
);

const LegacyChatbotRedirect = () => {
    const { chatbotId } = useParams();
    return <Navigate to={`/documents/chatbot/${encodeURIComponent(chatbotId || '')}`} replace />;
};

const AgentDetailRoute = () => {
    const { agentId } = useParams();
    return <Agents embedded agentId={agentId} />;
};

const ADMIN_TICKETS_CLIENT_ID = 'admin';

const AdminTicketsRoute = () => {
    const { t } = useTranslation();
    return (
    <Tickets
        title={t('tickets.adminPageTitle', 'Admin Tickets')}
        ticketBasePath="/admin/tickets"
        loadTickets={(params) => ticketService.getAdminTickets(ADMIN_TICKETS_CLIENT_ID, params)}
        createTicket={(payload) => ticketService.createAdminTicket(ADMIN_TICKETS_CLIENT_ID, payload)}
    />
    );
};

const AdminTicketDetailsRoute = () => {
    const { t } = useTranslation();
    return (
    <TicketDetails
        pageTitle={t('tickets.adminDetailTitle', 'Admin Ticket')}
        backPath="/admin/tickets"
        backLabel={t('tickets.backToAdminTickets', 'Back to Admin Tickets')}
        getTicket={(ticketId) => ticketService.getAdminTicket(ADMIN_TICKETS_CLIENT_ID, ticketId)}
        updateTicket={(ticketId, payload) => ticketService.updateAdminTicket(ADMIN_TICKETS_CLIENT_ID, ticketId, payload)}
        uploadAttachment={(ticketId, file, onProgress) => ticketService.uploadAdminTicketAttachment(ADMIN_TICKETS_CLIENT_ID, ticketId, file, onProgress)}
        deleteAttachment={(ticketId, fileId) => ticketService.deleteAdminTicketAttachment(ADMIN_TICKETS_CLIENT_ID, ticketId, fileId)}
        addNote={(ticketId, body) => ticketService.addAdminTicketNote(ADMIN_TICKETS_CLIENT_ID, ticketId, body)}
        assignmentEnabled
    />
    );
};

const featureEnabled = (features, key) => features.has(String(key ?? '').trim().toLowerCase());

const hasNetbotSupport = (features) => (
    featureEnabled(features, 'nebot.support') || featureEnabled(features, 'netbot.support')
);

const CustomerHomeRedirect = () => {
    const { can, hasFeature, planUpgradeable, loading } = useAccess();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    const netbot = hasFeature('nebot.support') || hasFeature('netbot.support');
    const canKb = can('kb');
    let target = '/settings';
    if (canKb && hasFeature('voiceflow.support')) target = '/data-management';
    else if (canKb && (netbot || planUpgradeable)) target = '/documents';
    else if (can('conversations') && netbot) target = '/nchistory';
    else if (can('livechat')) target = '/agent-requests';
    else if (can('tickets')) target = '/tickets';
    else if (can('members')) target = '/member-list';

    return <Navigate to={target} replace />;
};



// ─── Login screen ─────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => (
    <Box
        sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #0f172a 0%, #1e2a45 45%, #141927 100%)',
        }}
    >
        <WaveBg />

        {/* Subtle blue accent stripe */}
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: 'linear-gradient(90deg, #287bd6 0%, #3f8fe0 50%, #287bd6 100%)',
            }}
        />

        {/* Login card */}
        <Box
            sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: { xs: 3, sm: 5 },
                py: { xs: 4, sm: 5 },
                width: { xs: '90%', sm: 380 },
                background: 'rgba(255,255,255,0.04)',
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
            }}
        >
            {/* Logo */}
            <img
                src={netbotLogo}
                alt="NetBot"
                style={{ height: 56, width: 'auto', objectFit: 'contain', marginBottom: 16 }}
            />
            <Typography
                sx={{
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    letterSpacing: '0.2em',
                    color: '#c9a84c',
                    mb: 1,
                    textTransform: 'uppercase',
                }}
            >
                AI Platform
            </Typography>

            {/* Decorative divider */}
            <Box
                sx={{
                    width: 40,
                    height: 2,
                    borderRadius: 1,
                    background: 'linear-gradient(90deg, transparent, rgba(40,123,214,0.6), transparent)',
                    my: 2.5,
                }}
            />

            <Typography
                sx={{
                    fontSize: '0.88rem',
                    color: 'rgba(200,210,240,0.6)',
                    textAlign: 'center',
                    mb: 3.5,
                    lineHeight: 1.6,
                    px: 1,
                }}
            >
                Intelligent conversations, powerful automation
            </Typography>

            <Button
                fullWidth
                variant="contained"
                onClick={onLogin}
                sx={{
                    py: 1.4,
                    fontSize: '0.92rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #287bd6 0%, #1f6fca 100%)',
                    boxShadow: '0 4px 20px rgba(40,123,214,0.45)',
                    color: '#fff',
                    transition: 'all 0.22s ease',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #3f8fe0 0%, #287bd6 100%)',
                        boxShadow: '0 6px 28px rgba(40,123,214,0.6)',
                        transform: 'translateY(-1px)',
                    },
                    '&:active': { transform: 'translateY(0px)' },
                }}
            >
                Sign In
            </Button>

            <Typography
                sx={{
                    mt: 3,
                    fontSize: '0.7rem',
                    color: 'rgba(200,210,240,0.3)',
                    letterSpacing: '0.05em',
                }}
            >
                ネットボット · Secure Access
            </Typography>
        </Box>
    </Box>
);

// ─── Loading screen ───────────────────────────────────────────────────────────
const LoadingScreen = () => (
    <Box
        sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(145deg, #0f172a 0%, #1e2a45 100%)',
            gap: 2,
        }}
    >
        <CircularProgress size={36} sx={{ color: '#287bd6' }} />
        <Typography sx={{ color: 'rgba(200,210,240,0.5)', fontSize: '0.82rem', letterSpacing: '0.1em' }}>
            読み込み中…
        </Typography>
    </Box>
);

// ─── App ─────────────────────────────────────────────────────────────────────
const App = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
    const [loggedInUser,    setLoggedInUser]    = useState(authService.getCurrentUser());
    const [loggedInAs,      setLoggedInAs]      = useState(authService.getLoginAs());
    const [group,           setGroup]           = useState(authService.getGroup());
    const [loading,         setLoading]         = useState(true);

    function handleExpiredSession() {
        const accessToken = authService.getAccessToken();
        if (accessToken) {
            const decoded = jwtDecode(accessToken);
            if (decoded.exp < Math.floor(Date.now() / 1000)) {
                setIsAuthenticated(false);
                setLoggedInAs(null);
                setGroup(null);
                setLoggedInUser(null);
                authService.logout();
            }
        }
    }

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        if (code) {
            authService.parseCognitoResponse(window.location.href);
            setIsAuthenticated(authService.isAuthenticated());
            setLoggedInUser(authService.getCurrentUser());
            setGroup(authService.getGroup());
        }
        setLoading(false);
        handleExpiredSession();
        const intervalId = setInterval(handleExpiredSession, 10000);
        return () => clearInterval(intervalId);
    }, []);

    const handleLogin    = () => { authService.login(); };
    const handleLogout   = () => { authService.logout(); setIsAuthenticated(false); };
    const handleLoginAs  = (loginAs) => { authService.setLoginAs(loginAs); setLoggedInAs(loginAs); };
    const handleLogoutAs = () => { authService.setLoginAs(null); setLoggedInAs(null); };

    const renderContent = () => {
        const g = String(group ?? '');
        if (g === 'admin' && loggedInAs === null) {
            return (
                <Routes>
                    <Route path="/customer-list"                                   element={<Users key="customer" handleLoginAs={handleLoginAs} profile="customer" />} />
                    <Route path="/admin-setting"                                    element={<Users key="admin"    handleLoginAs={handleLoginAs} profile="admin"    />} />
                    <Route path="/admin/clients/:clientId/chatbots"                element={<AdminClientConfig initialSection="chatbots" />} />
                    <Route path="/admin/clients/:clientId/chatbots/new"            element={<AdminChatbotForm />} />
                    <Route path="/admin/clients/:clientId/chatbots/:chatbotId"     element={<AdminChatbotForm />} />
                    <Route path="/admin/clients/:clientId/agents"                  element={<AdminClientConfig initialSection="agents" />} />
                    <Route path="/admin/clients/:clientId/agents/new"              element={<AdminAgentForm />} />
                    <Route path="/admin/clients/:clientId/agents/:agentId"         element={<AdminAgentForm />} />
                    <Route path="/admin/clients/:clientId/bases"                   element={<BaseList />} />
                    <Route path="/admin/clients/:clientId/kbases"                  element={<AdminClientConfig initialSection="kbases" />} />
                    <Route path="/admin/clients/:clientId/config"                  element={<AdminClientConfig />} />
                    <Route path="/admin/clients/:clientId/config/:section"         element={<AdminClientConfig />} />
                    <Route path="/admin/clients/:clientId/profile"                 element={<AdminClientConfig initialSection="profile" />} />
                    <Route path="/plans"                                            element={<Plans />} />
                    <Route path="/permission-groups"                                element={<PermissionGroups />} />
                    <Route path="/vector-stores"                                   element={<VectorStores />} />
                    <Route path="/admin/tickets"                                    element={<AdminTicketsRoute />} />
                    <Route path="/admin/tickets/:ticketId"                          element={<AdminTicketDetailsRoute />} />
                    <Route path="/help-admin"                                       element={<AdminHelpCenter />} />
                    <Route path="/help"                                             element={<HelpCenter />} />
                    <Route path="/settings"                                         element={<Settings />} />
                    <Route path="/"                                                 element={<Navigate to="/customer-list" replace />} />
                    <Route path="*"                                                 element={<Navigate to="/customer-list" replace />} />
                </Routes>
            );
        }
        if (g === 'customer' || g === 'member' || loggedInAs !== null) {
            const KB = ['kb'];
            return (
                <Routes>
                    <Route path="/data-management"                                 element={<RequirePermission anyOf={KB}><Files /></RequirePermission>} />
                    <Route path="/kbases"                                           element={<RequirePermission anyOf={KB}><CustomerKbases /></RequirePermission>} />
                    <Route path="/kbases/:kbaseId/documents"                       element={<RequirePermission anyOf={KB}><KbaseWorkspace /></RequirePermission>} />
                    <Route path="/kbases/:kbaseId/feedback"                        element={<RequirePermission anyOf={KB}><KbaseWorkspace /></RequirePermission>} />
                    <Route path="/kbases/:kbaseId/documents/:folderName"           element={<RequirePermission anyOf={KB}><KbaseDocuments /></RequirePermission>} />
                    <Route path="/conversations"                                    element={<RequirePermission anyOf={KB}><Conversations /></RequirePermission>} />
                    <Route path="/nchistory"                                        element={<RequirePermission anyOf={['conversations']}><ConversationHistory /></RequirePermission>} />
                    <Route path="/tickets"                                          element={<RequirePermission anyOf={['tickets']}><Tickets /></RequirePermission>} />
                    <Route path="/tickets/:ticketId"                               element={<RequirePermission anyOf={['tickets']}><TicketDetails assignmentEnabled assigneeProfile="member" /></RequirePermission>} />
                    <Route path="/support-tickets/:supportTicketId"                 element={<RequirePermission anyOf={['tickets']}><SupportTicketDetails /></RequirePermission>} />
                    <Route path="/voicebots" element={<RequirePermission anyOf={['agents']} feature={['voice_enable']}><VoiceBots /></RequirePermission>} />
                    <Route path="/agents"                                           element={<RequirePermission anyOf={['agents']}><Agents autoOpenSingle /></RequirePermission>} />
                    <Route path="/agents/:agentId"                                  element={<RequirePermission anyOf={['agents']}><AgentDetailRoute /></RequirePermission>} />
                    <Route path="/agent-requests"                                   element={<RequirePermission anyOf={['livechat']}><AgentRequests /></RequirePermission>} />
                    <Route path="/documents"                                        element={<RequirePermission anyOf={KB}><Chatbot /></RequirePermission>} />
                    <Route path="/documents/feedback"                               element={<RequirePermission anyOf={KB}><Chatbot /></RequirePermission>} />
                    <Route path="/documents/chatbot/:chatbotId"                    element={<RequirePermission anyOf={KB}><Chatbot /></RequirePermission>} />
                    <Route path="/documents/chatbot/:chatbotId/feedback"           element={<RequirePermission anyOf={KB}><Chatbot /></RequirePermission>} />
                    <Route path="/chatbot-settings"                                element={<RequirePermission anyOf={['chatbots']}><Chatbots editBasePath="/chatbot-settings/chatbot" autoOpenSingle /></RequirePermission>} />
                    <Route path="/chatbot-settings/chatbot/:chatbotId"             element={<RequirePermission anyOf={['chatbots']}><Chatbot settingsMode /></RequirePermission>} />
                    <Route path="/chatbots"                                         element={<Navigate to="/documents" replace />} />
                    <Route path="/chatbots/chatbot/:chatbotId"                     element={<LegacyChatbotRedirect />} />
                    <Route path="/usage"                                            element={<Usage />} />
                    <Route path="/audit-logs" element={g === 'customer' || (g === 'admin' && loggedInAs) ? <AuditLogs key={loggedInAs || loggedInUser} /> : <Navigate to="/settings" replace />} />
                    <Route path="/nanalytics"                                      element={<RequirePermission anyOf={['analytics']}><Analytics /></RequirePermission>} />
                    <Route path="/member-list"                                      element={<RequirePermission anyOf={['members']}><Users key="admin" handleLoginAs={handleLoginAs} profile="member" /></RequirePermission>} />
                    <Route path="/help"                                             element={<HelpCenter />} />
                    <Route path="/settings"                                         element={<Settings />} />
                    <Route path="/"                                                 element={<CustomerHomeRedirect />} />
                    <Route path="*"                                                 element={<CustomerHomeRedirect />} />
                </Routes>
            );
        }
return null;
    };

    if (loading) return <ThemeProviderWrapper><LoadingScreen /></ThemeProviderWrapper>;
    const isPublicSupportTicketPath = /^\/customer-support-tickets\/[^/]+\/?$/.test(window.location.pathname);

    return (
        <ThemeProviderWrapper>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                {isPublicSupportTicketPath ? (
                    <Box
                        sx={{
                            minHeight: '100vh',
                            p: 3,
                            backgroundColor: 'background.default',
                        }}
                    >
                        <Routes>
                            <Route path="/customer-support-tickets/:accessToken" element={<PublicSupportTicketDetails />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Box>
                ) : isAuthenticated ? (
                    <AccessProvider group={group} loginAs={loggedInAs}>
                    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                        <Sidebar
                            userName={loggedInUser}
                            group={group}
                            loginAs={loggedInAs}
                            onLogout={handleLogout}
                            onLogoutAs={handleLogoutAs}
                        />
                        <HelpProvider loginAs={loggedInAs}>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    p: 3,
                                    minHeight: '100vh',
                                    backgroundColor: 'background.default',
                                }}
                            >
                                {renderContent()}
                            </Box>
                            <WelcomeVideoModal
                                group={group}
                                loginAs={loggedInAs}
                                userName={loggedInUser}
                            />
                        </HelpProvider>
                    </Box>
                    </AccessProvider>
                ) : (
                    <LoginScreen onLogin={handleLogin} />
                )}
            </Router>
        </ThemeProviderWrapper>
    );
};

export default App;
